import { environment } from './../environments/environment.prd';
import { firstValueFrom } from 'rxjs';
import { Component } from '@angular/core';
import { Workbook } from 'exceljs';
import { CognitoService } from '../service/cognito.service';
import { ClientService, Client } from '../service/client.service';
import { FormBuilder, Validators } from '@angular/forms';
import { ChartService } from '../service/chart.service';
import { InvoiceService, User, Logs, Bin, SubGroup, DeviceLogSummary } from '../service/invoice.service';
import { ThemeService } from '../service/theme.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-customer-report',
    templateUrl: './customer-report.component.html',
    styleUrls: [
        '../../charts.css',
        './customer-report.component.css',
        '../../global-elements.css',
    ],
    standalone: false
})
export class CustomerReportComponent {
  public clients: Client[] = [];
  public selectedClient: string = '';
  public devices: string[] = [];
  public users: User[] = [];
  public logs: Logs[] = [];
  public bins: Bin[] = [];
  public subGroups: SubGroup[] = [];
  public logRows: number = 0;
  public deviceLogSummaries: DeviceLogSummary[] = [];
  public showFilters = true;
  public showStatistics = false;

  public clientInformation: Client | undefined = undefined;
  // Invoice filter form
  public invoice = this.fb.group({
    client_name: ['', Validators.required],
    from: [null as number | null],
    to: [null as number | null],
  });
  constructor(
    private cognitoService: CognitoService,
    private clientService: ClientService,
    private fb: FormBuilder,
    public charts: ChartService,
    public invoiceService: InvoiceService,
    public theme: ThemeService,
    private translate: TranslateService
  ) {
      this.cognitoService.confirmValidUser();
  }

  // Load the list of clients when the page initializes.
  async ngOnInit() {
    //Verifies that the current User is allowed to view this component
    this.cognitoService.getCurrentRole(
      [environment.users.role.administrator],
      [environment.users.superAdmin]
    );

    this.clients = await this.clientService.fetchClients();
  }

  // Submit the filter form, fetch the report, and render statistics and charts.
  async submit() {
    const invoice = this.invoice.value;
    console.log(invoice.client_name, invoice.from, invoice.to);

    //If selecteced client is not null
    if (invoice.client_name) {

      //Fetch client information
      const report = await firstValueFrom(
        this.invoiceService.invoiceReport(
          invoice.client_name,
          invoice.from ?? 0,
          invoice.to ?? 0
        )
      );

      //Load client information from array into an object for display use
      this.clientInformation = this.getClient(invoice.client_name);

      console.log(this.clientInformation);

      //Define properties for display
      this.devices = report.devices;
      this.users = report.users;
      this.bins = report.bins;
      this.subGroups = report.sub_groups;
      this.logs = report.logs;
      this.selectedClient = this.clientInformation?.client_name ?? '';
      this.logRows = this.sumLogVolume();
      this.deviceLogSummaries = this.buildDeviceLogSummaries();

      //Show-Hide form/statistics
      this.showStatistics = true;
      this.showFilters = false;
      await this.updateCharts();

      //Necessary evil. Charts are re-used and have some css that hides them for other pages.
      const containers = document.querySelectorAll('.chart-container');
      containers.forEach((el) => ((el as HTMLElement).style.display = 'block'));
    }
  }

  // Find a client by id from the selected client
  getClient(id: number | string): Client | undefined {
    const match = this.clients.find((c) => c.client_id === id);
    return match;
  }

  // Sum log volume across all logs and return the total in megabytes.
  sumLogVolume(): number {
    let logRows = 0;
    for (let row of this.logs) {
      logRows += row.estimated_bytes;
    }

    //return in MB
    return Math.round(logRows / 1024 / 1024);
  }

  // Build a per-device summary that combines logs, bins, subgroups, and sizes.
  private buildDeviceLogSummaries(): DeviceLogSummary[] {
    if (!this.logs?.length) {
      return [];
    }

    const binByThing = new Map<string, string>();
    for (const bin of this.bins) {
      if (bin.thing_name) {
        binByThing.set(bin.thing_name, bin.bin_name || bin.bin_id);
      }
    }

    const subgroupByThing = new Map<string, string>();
    for (const group of this.subGroups) {
      if (!group.devices) {
        continue;
      }
      for (const device of group.devices) {
        subgroupByThing.set(device.thing_name, group.sub_group_name);
      }
    }

    return this.logs
      .map((log) => ({
        thingName: log.thing_name,
        binName: binByThing.get(log.thing_name) ?? 'N/A',
        logRows: log.log_count,
        logSizeMb: this.bytesToMb(log.estimated_bytes),
        subGroup: subgroupByThing.get(log.thing_name) ?? 'N/A',
      }))
      .sort((a, b) => a.thingName.localeCompare(b.thingName));
  }

  // Update line and bar chart data using the current device log summaries.
  private async updateCharts(): Promise<void> {
    const logSeries = this.deviceLogSummaries.map((summary) => ({
      name: summary.thingName,
      value: summary.logRows,
    }));
    const chartLabels = await firstValueFrom(
      this.translate.get([
        'customerReport.charts.logSeriesLabel',
        'customerReport.charts.subgroupLegend',
      ])
    );
    const logSeriesLabel =
      chartLabels['customerReport.charts.logSeriesLabel'];
    this.charts.logLineChartData = [
      {
        name: logSeriesLabel,
        series: logSeries,
      },
    ];
    this.charts.logLineYAxisLabel = logSeriesLabel;
    const labelInterval =
      logSeries.length > 25 ? Math.ceil(logSeries.length / 25) : 1;
    this.charts.configureLogLineXAxis(
      logSeries.map((point) => point.name),
      labelInterval
    );
    const dynamicWidth = Math.min(1800, Math.max(700, logSeries.length * 25));
    this.charts.logLineChartView = [dynamicWidth, 400];

    const subgroupCounts = new Map<string, number>();
    this.deviceLogSummaries.forEach((summary) => {
      const key = summary.subGroup || 'N/A';
      subgroupCounts.set(key, (subgroupCounts.get(key) ?? 0) + 1);
    });

    const subGroupSeries = Array.from(subgroupCounts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.charts.singleBarChart = subGroupSeries;
    this.charts.showLegendBarChart = false;
    this.charts.showXAxisBarChart = true;
    this.charts.showYAxisBarChart = true;
    this.charts.viewBarChart = [500, 400];
    this.charts.legendTitleBarChart =
      chartLabels['customerReport.charts.subgroupLegend'];
  }

  // Export the customer report and detailed logs to an Excel workbook.
  async exportCustomerReport(): Promise<void> {
    if (!this.deviceLogSummaries.length) {
      return;
    }

    const translationKeys = [
      'customerReport.export.summarySheet',
      'customerReport.export.logsSheet',
      'customerReport.export.fieldHeader',
      'customerReport.export.valueHeader',
      'customerReport.summary.name',
      'customerReport.summary.address',
      'customerReport.summary.contactEmail',
      'customerReport.summary.contactPhone',
      'customerReport.summary.numberOfUsers',
      'customerReport.summary.numberOfDevices',
      'customerReport.summary.numberOfBins',
      'customerReport.summary.numberOfSubGroups',
      'customerReport.summary.logVolumeMb',
      'customerReport.table.device',
      'customerReport.table.bin',
      'customerReport.table.logs',
      'customerReport.table.megabytes',
      'customerReport.table.subgroup',
    ];
    const text = await firstValueFrom(this.translate.get(translationKeys));

    const workbook = new Workbook();
    const summarySheet = workbook.addWorksheet(
      text['customerReport.export.summarySheet']
    );
    const summaryRows: Array<[string, string | number]> = [
      [text['customerReport.summary.name'], this.clientInformation?.client_name ?? ''],
      [text['customerReport.summary.address'], this.clientInformation?.address ?? ''],
      [text['customerReport.summary.contactEmail'], this.clientInformation?.email ?? ''],
      [text['customerReport.summary.contactPhone'], this.clientInformation?.phone_number ?? ''],
      [text['customerReport.summary.numberOfUsers'], this.users.length],
      [text['customerReport.summary.numberOfDevices'], this.devices.length],
      [text['customerReport.summary.numberOfBins'], this.bins.length],
      [text['customerReport.summary.numberOfSubGroups'], this.subGroups.length],
      [text['customerReport.summary.logVolumeMb'], this.logRows],
    ];
    summarySheet.addRows(summaryRows);

    const logSheet = workbook.addWorksheet(
      text['customerReport.export.logsSheet']
    );
    logSheet.columns = [
      { header: text['customerReport.table.device'], width: 35 },
      { header: text['customerReport.table.bin'], width: 35 },
      { header: text['customerReport.table.logs'], width: 18 },
      { header: text['customerReport.table.megabytes'], width: 25 },
      { header: text['customerReport.table.subgroup'], width: 25 },
    ];
    logSheet.addRows(
      this.deviceLogSummaries.map((summary) => [
        summary.thingName,
        summary.binName,
        summary.logRows,
        summary.logSizeMb,
        summary.subGroup,
      ])
    );

    const filename = `customer-report-${this.selectedClient || 'client'}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }


  // Convert a byte count to megabytes with two decimal precision.
  private bytesToMb(bytes: number): number {
    if (!bytes || bytes <= 0) {
      return 0;
    }
    return Number((bytes / 1024 / 1024).toFixed(2));
  }

  // Toggle between the filter form and the statistics view.
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
    this.showStatistics = !this.showStatistics;
  }
}
